/**
 * The container scalars a save sends, and what blocks a save. A follower
 * sends its own campaign (one of its master config's group) but never the
 * scalars of inherited sections; a master has no schedule (Phase 14).
 * For Media Devoted | Created By Ciro Autiero
 */
import { cleanHourFormula, type DropGenSection, lanesProblems } from '@trafficpulse/shared';

import type { ContainerDraft, DropGenHourOptimizerConfig } from '../../../../drop-generator.types';
import { autoSendProblem } from '../../autosend/autosend-slice';

/** The PATCH/POST body for the container scalars. A follower never sends
*/
export function draftToContainerBody(draft: ContainerDraft) {
  const following = draft.kind !== 'MASTER' && draft.masterId !== null;
  const own = (s: DropGenSection) => !following || draft.ownSections[s] === true;
  if (draft.kind === 'MASTER') return masterBody(draft);
  return {
    name: draft.name.trim(),
    color: draft.color,
    staticTagId: draft.staticTagId,
    startMode: draft.startMode,
    gapMin: draft.gapMin,
    startAtMin: draft.startAtMin,
    endAtMin: draft.endAtMin,
    intervalMin: draft.intervalMin,
    runDays: draft.runDays,
    weekendAmountPct: draft.weekendAmountPct,
    sendOutsideWindow: draft.sendOutsideWindow ?? false,
    ...(draft.kind === 'ROUTE_TEST' ? { kind: 'ROUTE_TEST' as const } : {}),
    ...(draft.campaignWiserId !== null ? { campaignWiserId: draft.campaignWiserId } : {}),
    ...(own('amount') ? { defaultAmount: draft.defaultAmount } : {}),
    ...(own('failMode') ? { failMode: draft.failMode } : {}),
    ...(own('verdict') ? { scalingEnabled: draft.scalingEnabled } : {}),
    ...(own('hourOptimizer') ? { hourOptimizerJson: postableHourOptimizer(draft.hourOptimizer) } : {}),
  };
}

/** A master has no schedule: only the shared scalars travel. */
export function masterBody(draft: ContainerDraft) {
  return {
    name: draft.name.trim(),
    color: draft.color,
    campaignWiserId: draft.campaignWiserId,
    defaultAmount: draft.defaultAmount,
    failMode: draft.failMode,
    scalingEnabled: draft.scalingEnabled,
    hourOptimizerJson: postableHourOptimizer(draft.hourOptimizer),
  };
}

export function validateDraft(draft: ContainerDraft): string | null {
  if (draft.kind === 'MASTER') {
    if (!draft.name.trim()) return 'Give the master a name';
    return draft.campaignWiserId ? (poolProblem(draft) ?? autoSendProblem(draft)) : 'Pick a campaign';
  }
  if (!draft.name.trim()) return 'Give the container a name';
  if (!draft.masterId && !draft.campaignWiserId) return 'Pick a master';
  if (!draft.campaignWiserId) return 'Pick the campaign it runs on';
  if (draft.runDays.length === 0) return 'Pick at least one day it runs';
  if (draft.startMode === 'FIXED_TIME' && draft.startAtMin === null)
    return 'Fixed start needs a time';
  if (
    draft.startMode === 'FIXED_TIME' &&
    draft.startAtMin !== null &&
    draft.endAtMin !== null &&
    draft.endAtMin <= draft.startAtMin
  )
    return 'End must be after start';
  return poolProblem(draft) ?? autoSendProblem(draft);
}

/** Phase 40 — two lanes of one gateway with the same sender IDs: the API refuses the save with this very sentence. */
const poolProblem = (draft: ContainerDraft): string | null => lanesProblems(draft.gateway.pool)[0] ?? null;

/** Slider rows still waiting for a metric never reach the API (400 there). */
function postableHourOptimizer(config: DropGenHourOptimizerConfig): DropGenHourOptimizerConfig {
  return config.formula ? { ...config, formula: cleanHourFormula(config.formula) } : config;
}
